import { addEtDays, getETDate } from '@/lib/marketTime';
import { MARKET_HOLIDAYS } from '@/lib/marketHolidays';

/**
 * The blue D — one rule for every place it shows: watchlist rows, the ticker
 * popup's badge cluster, the stock / ETF header pill and the chart's corner D.
 *
 * On from 2 trading days before the ex-dividend date through 1 trading day
 * after it. Trading days, not calendar days: the VistaShares Target 15 funds
 * go ex on a Monday, and the Friday before is the last day to buy in for the
 * distribution — a calendar-day window would skip exactly that Friday.
 *
 * Pure logic only. The badge colours live in
 * components/shared/DividendBadge.tsx: Tailwind doesn't scan src/lib, so class
 * strings kept here would never be generated.
 */
export const DIVIDEND_BADGE_DAYS_BEFORE = 2;
export const DIVIDEND_BADGE_DAYS_AFTER = 1;

export type DividendBadgePhase = 'upcoming' | 'today' | 'after';

/** What a badge needs to know about one ex-dividend date. */
export interface DividendSoon {
  exDate: string;              // YYYY-MM-DD
  amount: number | null;       // null = scheduled, not declared yet
  paymentDate: string | null;
  projected: boolean;          // issuer cadence carried forward, not announced
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;

function isTradingDay(ymd: string): boolean {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow !== 0 && dow !== 6 && !(ymd in MARKET_HOLIDAYS);
}

/** Shift `ymd` by `n` trading days (negative = back). Starting from a weekend
 *  or holiday, the first step lands on the nearest trading day that way. */
export function addTradingDays(ymd: string, n: number): string {
  const step = n < 0 ? -1 : 1;
  let d = ymd;
  for (let left = Math.abs(n); left > 0;) {
    d = addEtDays(d, step);
    if (isTradingDay(d)) left--;
  }
  return d;
}

/** Signed trading-day distance from `today` to `exDate`: 0 on the ex-date, 1
 *  the trading day before it (a Friday for a Monday ex-date, and the weekend in
 *  between), -1 the trading day after. Null for an unparseable date or one
 *  more than ~2 months away — far outside any badge window.
 *
 *  An ex-date that isn't itself a trading day (bad feed data) counts as the
 *  next trading day, when the stock actually starts trading ex. Left as-is it
 *  would score 0 from BOTH neighbouring trading days and read "today" on each. */
export function tradingDaysUntil(exDate: string, today = getETDate()): number | null {
  if (!YMD.test(exDate) || !YMD.test(today)) return null;
  const ex = isTradingDay(exDate) ? exDate : addTradingDays(exDate, 1);
  if (ex === today) return 0;
  const step = ex > today ? 1 : -1;
  let n = 0;
  let d = today;
  for (let i = 0; i < 60; i++) {
    d = addEtDays(d, step);
    if (isTradingDay(d)) n += step;
    if (d === ex) return n;
  }
  return null;
}

/** Calendar days apart, for a cheap reject before counting trading days. */
function calendarDaysApart(a: string, b: string): number {
  return Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000;
}

/** Where `exDate` sits relative to today for the badge, or null when outside
 *  the window (no badge). */
export function dividendBadgePhase(exDate: string, today = getETDate()): DividendBadgePhase | null {
  // The widest the window gets is ~5 calendar days (2 trading days ahead over
  // a long holiday weekend), so anything further out is rejected without the
  // day-by-day walk — the chart runs this over two years of weekly payers.
  if (!(calendarDaysApart(exDate, today) <= 9)) return null;
  const n = tradingDaysUntil(exDate, today);
  if (n == null) return null;
  if (n === 0) return 'today';
  if (n > 0 && n <= DIVIDEND_BADGE_DAYS_BEFORE) return 'upcoming';
  if (n < 0 && -n <= DIVIDEND_BADGE_DAYS_AFTER) return 'after';
  return null;
}

/** The ex-date range worth fetching for today's badges: exactly the window,
 *  in trading days, so the shared /dividends request stays a few days wide. */
export function getDividendBadgeWindow(today = getETDate()): { from: string; to: string } {
  return {
    from: addTradingDays(today, -DIVIDEND_BADGE_DAYS_AFTER),
    to: addTradingDays(today, DIVIDEND_BADGE_DAYS_BEFORE),
  };
}

/** One badge-worthy ex-date per symbol — the nearest to today, an upcoming one
 *  winning a tie with one just passed. */
export function pickDividendBadgeRows(
  rows: ReadonlyArray<DividendSoon & { symbol: string }>,
  today = getETDate(),
): Map<string, DividendSoon> {
  const best = new Map<string, { info: DividendSoon; n: number }>();
  for (const r of rows) {
    if (!dividendBadgePhase(r.exDate, today)) continue;
    const n = tradingDaysUntil(r.exDate, today)!;
    const symbol = r.symbol.toUpperCase();
    const prev = best.get(symbol);
    if (!prev || Math.abs(n) < Math.abs(prev.n) || (Math.abs(n) === Math.abs(prev.n) && n > prev.n)) {
      best.set(symbol, {
        info: { exDate: r.exDate, amount: r.amount, paymentDate: r.paymentDate, projected: r.projected },
        n,
      });
    }
  }
  return new Map([...best].map(([s, v]) => [s, v.info]));
}

function weekday(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}

function shortDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)));
}

/** Header-pill wording: "Ex-div today" / "Ex-div Mon" / "Ex-div was Mon".
 *  Null outside the window. Weekday names rather than "tomorrow": on a Friday,
 *  a Monday ex-date is the next trading day, not tomorrow. */
export function dividendBadgeWhen(exDate: string, today = getETDate()): string | null {
  const phase = dividendBadgePhase(exDate, today);
  if (phase === 'today') return 'Ex-div today';
  if (phase === 'upcoming') return `Ex-div ${weekday(exDate)}`;
  if (phase === 'after') return `Ex-div was ${weekday(exDate)}`;
  return null;
}

/** Tooltip for the glyph: the date and its distance, the amount (or TBD), the
 *  pay date, and a projected flag — colour is never the only channel. */
export function formatDividendBadgeTooltip(info: DividendSoon, today = getETDate()): string {
  const n = tradingDaysUntil(info.exDate, today);
  const when = n === 0 ? 'today'
    : n === 1 ? 'next trading day'
    : n === -1 ? 'previous trading day'
    : n != null && n > 0 ? `in ${n} trading days`
    : null;
  const parts = [`Ex-dividend ${shortDate(info.exDate)}${when ? ` (${when})` : ''}`];
  parts.push(info.amount != null ? `$${info.amount}/sh` : 'amount TBD');
  if (info.paymentDate) parts.push(`pays ${shortDate(info.paymentDate)}`);
  if (info.projected) parts.push('projected from the fund\'s schedule, not announced yet');
  return parts.join(' · ');
}
